import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';

import TopBanner from 'components/common/TopBanner';
import useCookieConsent from 'hooks/useCookieConsent';
import {
  getFcmPermissionState,
  getFcmPermissionDismissedUntil,
  getStoredFcmToken,
  isFcmPushEnabled,
  setFcmPushEnabled,
  setFcmPermissionState,
  setFcmPermissionDismissedUntil,
  useFcmToken,
} from 'hooks/useFcmToken';
import { RootState } from 'redux/store';

import useCurrentUser from '../hooks/useCurrentUser';

const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
const NotificationPermissionDialog = (): JSX.Element => {
  const accessToken = useSelector((state: RootState) => state.user?.accessToken);
  const { syncFcmRegistration } = useFcmToken();
  const {
    isConfigured: isCookieConsentConfigured,
    isReady: isCookieConsentReady,
    isCategoryAccepted,
    openPanel: openCookiePreferencesPanel,
  } = useCookieConsent();
  const isRequestingRef = useRef(false);
  const previousPermissionRef = useRef<NotificationPermission | null>(null);
  const [open, setOpen] = useState(false);
  const [showCookieRequirement, setShowCookieRequirement] = useState(false);
  const currentUser = useCurrentUser();
  const canUseFunctionalCookies = !isCookieConsentConfigured || isCategoryAccepted('functional');
  const shouldRequestCookieConsent = isCookieConsentConfigured && !canUseFunctionalCookies;
  const evaluateNotificationPermission = useCallback(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (!accessToken) {
      setOpen(false);
      return;
    }
    if (isCookieConsentConfigured && !isCookieConsentReady) {
      setOpen(false);
      return;
    }
    if (isRequestingRef.current) return;
    const currentPermission = Notification.permission;
    const storedPermission = getFcmPermissionState();

    const previousPermission = previousPermissionRef.current ?? storedPermission;
    if (currentPermission === 'default' && previousPermission && previousPermission !== 'default') {
      setFcmPermissionDismissedUntil(0);
    }
    previousPermissionRef.current = currentPermission;
    setFcmPermissionState(currentPermission);

    if (currentPermission === 'denied') {
      if (getStoredFcmToken()) {
        syncFcmRegistration().catch(() => {});
      }
      setOpen(false);
      return;
    }
    if (!isFcmPushEnabled()) {
      setOpen(false);
      return;
    }

    if (currentPermission === 'granted') {
      syncFcmRegistration().catch(() => {});
      setOpen(false);
      return;
    }
    if (currentPermission !== 'default') {
      setOpen(false);
      return;
    }
    if (Date.now() < getFcmPermissionDismissedUntil()) {
      setOpen(false);
      return;
    }
    setOpen(true);
  }, [accessToken, currentUser, isCookieConsentConfigured, isCookieConsentReady, syncFcmRegistration]);

  useEffect(() => {
    evaluateNotificationPermission();

    const handleFocus = () => evaluateNotificationPermission();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        evaluateNotificationPermission();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [evaluateNotificationPermission]);

  useEffect(() => {
    if (canUseFunctionalCookies) {
      setShowCookieRequirement(false);
    }
  }, [canUseFunctionalCookies]);

  const handleAccept = async () => {
    isRequestingRef.current = true;
    try {
      const permission = await Notification.requestPermission();
      setOpen(false);
      setFcmPermissionState(permission);
      if (permission === 'granted') {
        setFcmPushEnabled(true);
        syncFcmRegistration().catch(() => {});
      } else {
        syncFcmRegistration().catch(() => {});
      }
    } finally {
      isRequestingRef.current = false;
    }
  };

  const handleAcceptClick = () => {
    if (shouldRequestCookieConsent) {
      if (showCookieRequirement) {
        openCookiePreferencesPanel();
        return;
      }

      setShowCookieRequirement(true);
      return;
    }

    handleAccept().catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Unhandled error in handleAccept:', error);
    });
  };

  const handleDismiss = () => {
    setFcmPermissionDismissedUntil(DISMISS_MS);
    setShowCookieRequirement(false);
    setOpen(false);
  };

  const isShowingCookieRequirement = shouldRequestCookieConsent && showCookieRequirement;

  return (
    <TopBanner
      open={open}
      icon={<NotificationsNoneIcon />}
      message={
        isShowingCookieRequirement
          ? 'Para activar notificaciones, activa primero las cookies de preferencias o personalización'
          : 'Activa las notificaciones en este dispositivo'
      }
      actionLabel={isShowingCookieRequirement ? 'Configurar cookies' : 'Activar'}
      actionDisabled={isShowingCookieRequirement && !isCookieConsentReady}
      onAction={handleAcceptClick}
      onClose={handleDismiss}
    />
  );
};

export default NotificationPermissionDialog;
